import { Camera, Video, Lightbulb, Mic, Mail, Phone, MapPin, Instagram, Twitter, Linkedin } from 'lucide-react'
import { useQuery } from 'react-query'
import { apiService as api } from '../services/api'
import packageJson from '../../package.json'
import { getImageUrl } from '../utils/apiUrl'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const appVersion = packageJson.version || import.meta.env.VITE_REACT_APP_VERSION || process.env.REACT_APP_VERSION || ''

  const { data: companyPublic } = useQuery(
    'company-public-footer',
    () => api.get('/rentman/company/public').then(res => res.data),
    { staleTime: 10 * 60 * 1000 }
  )

  const email = companyPublic?.email || ''
  const phone = companyPublic?.phone_mobile || companyPublic?.phone_landline || ''
  const address = companyPublic?.address || ''

  // Catégories de l'accueil (même ordre que la home page)
  const categories = [
    {
      name: 'Images',
      icon: Camera,
      href: '/catalogue-materiels?parent_category=image'
    },
    {
      name: 'Éclairage',
      icon: Lightbulb,
      href: '/catalogue-materiels?parent_category=lumiere'
    },
    {
      name: 'Machinerie',
      icon: Mic,
      href: '/catalogue-materiels?parent_category=machinerie'
    },
    {
      name: 'Énergie',
      icon: Lightbulb,
      href: '/catalogue-materiels?parent_category=energie'
    },
    {
      name: 'Régie',
      icon: Video,
      href: '/catalogue-materiels?parent_category=la-regie'
    },
    {
      name: 'Audio',
      icon: Mic,
      href: '/catalogue-materiels?parent_category=audio'
    }
  ]

  return (
    <footer className="bg-white border-t border-gray-200 shadow-lg hidden md:block">
      <div className="container-custom">
        <div className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Logo et description */}
            <div className="lg:col-span-2">
              <div className="flex items-center mb-4">
                <div className="h-10 w-auto">
                          {companyPublic?.company?.logo_path ? (
                    <img 
                              src={`${getImageUrl(companyPublic.company.logo_path)}?v=${packageJson.version}`}
                      alt={`${companyPublic.company.name || 'FlightCase'} Logo`}
                      className="h-full w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <img 
                              src={`${getImageUrl('/images/logo/flightcase-logo.jpg')}?v=${packageJson.version}`}
                      alt="FlightCase Logo" 
                      className="h-full w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  )}
                  <div className="h-10 w-10 bg-gray-800 rounded-lg items-center justify-center hidden">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
              <p className="text-gray-600 mb-6 max-w-md">
                Votre partenaire pour la location de matériel cinéma et audiovisuel en Guadeloupe.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/FlightcaseFWI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-orange-100 rounded-lg text-orange-600 hover:text-white hover:bg-orange-600 transition-colors"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/flightcase.fwi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-orange-100 rounded-lg text-orange-600 hover:text-white hover:bg-orange-600 transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/flightcase-fwi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-orange-100 rounded-lg text-orange-600 hover:text-white hover:bg-orange-600 transition-colors"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a
                  href="https://wa.me/590590265430"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-orange-100 rounded-lg text-orange-600 hover:text-white hover:bg-orange-600 transition-colors"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Catégories */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Catalogue</h3>
              <ul className="space-y-1">
                {categories.map((category) => (
                  <li key={category.name}>
                    <a
                      href={category.href}
                      className="text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      {category.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact</h3>
              <div className="space-y-3">
                {email && (
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-600" />
                    <span className="text-gray-600">{email}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-600" />
                    <span className="text-gray-600">{phone}</span>
                  </div>
                )}
                {address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 leading-snug">{address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-600 text-sm">
                Flightcase © {currentYear} – {appVersion ? `v${appVersion}` : ''}
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a
                  href="/mentions-legales"
                  className="text-gray-600 hover:text-gray-800 text-sm transition-colors"
                >
                  Mentions légales
                </a>
                <a
                  href="/politique-confidentialite"
                  className="text-gray-600 hover:text-gray-800 text-sm transition-colors"
                >
                  Politique de confidentialité
                </a>
                <a
                  href="/cgv"
                  className="text-gray-600 hover:text-gray-800 text-sm transition-colors"
                >
                  CGV
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
